import { zodResolver } from '@hookform/resolvers/zod';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { z } from 'zod';

import { TotemMark, TotemMarkOutline, TotemWordmark } from '@/shared/brand/TotemLogo';
import { Button } from '@/shared/ui/button';
import { Label } from '@/shared/ui/label';
import { Input } from '@/shared/ui/input';
import { SecretInput } from '@/shared/ui/secret-input';
import { useAuthStore } from '@/stores/authStore';
import { useWorkstationStore } from '@/stores/workstationStore';

import { useLogin } from './useAuth';

const login_schema = z.object({
  email: z.string().min(1).max(160),
  password: z.string().min(1).max(255),
});

type LoginFields = z.infer<typeof login_schema>;

export function LoginPage(): JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const access_token = useAuthStore((s) => s.access_token);
  const workstation_id = useWorkstationStore((s) => s.workstation_id);
  const workstation_name = useWorkstationStore((s) => s.display_name);
  const { submitLogin, loading } = useLogin();
  const [submit_error, setSubmitError] = useState<string | null>(null);

  const form = useForm<LoginFields>({
    resolver: zodResolver(login_schema),
    defaultValues: { email: '', password: '' },
  });

  if (!workstation_id) {
    return <Navigate to="/setup/workstation" replace />;
  }
  if (access_token) {
    const next = (location.state as { from?: { pathname?: string } } | null)?.from?.pathname;
    return <Navigate to={next ?? '/app'} replace />;
  }

  const onSubmitLogin = async (values: LoginFields): Promise<void> => {
    setSubmitError(null);
    try {
      await submitLogin(values.email.trim(), values.password);
      navigate('/app');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'invalid_credentials';
      setSubmitError(
        message.includes('user_locked')
          ? t('auth.user_locked')
          : t('auth.invalid_credentials'),
      );
    }
  };

  return (
    <main className="grid min-h-full lg:grid-cols-[1.05fr_0.95fr]">
      {/* Brand hero — drenched navy rail, outline mascot. Hidden on small screens. */}
      <aside className="totem-rail relative hidden flex-col justify-between overflow-hidden p-12 text-white lg:flex">
        <div className="totem-dots pointer-events-none absolute inset-0 text-white/[0.06]" aria-hidden />
        <div className="relative flex flex-1 flex-col items-start justify-center gap-6">
          <TotemMarkOutline className="h-36 w-auto text-white drop-shadow-[0_18px_30px_rgba(0,0,0,0.28)]" title={t('common.app_name')} />
          <div className="space-y-3">
            <TotemWordmark className="h-11 w-auto text-white" title="tótem" />
            <p className="text-[11px] font-bold uppercase tracking-[0.24em] text-white/80">
              {t('common.tagline')}
            </p>
          </div>
        </div>
        <p className="relative max-w-sm text-[15px] leading-relaxed text-white/85">
          {t('auth.hero_lede')}
        </p>
      </aside>

      {/* Form side */}
      <section className="flex items-center justify-center bg-background px-6 py-12">
        <div className="w-full max-w-sm animate-totem-in">
          <div className="mb-8">
            <TotemMark className="mb-5 h-11 w-auto text-primary lg:hidden" title={t('common.app_name')} />
            <h1 className="font-display text-2xl font-extrabold tracking-tight text-foreground">
              {t('auth.welcome')}
            </h1>
            <p className="mt-1.5 text-sm text-muted-foreground">{t('auth.welcome_subtitle')}</p>
          </div>

          <form onSubmit={form.handleSubmit(onSubmitLogin)} className="space-y-4" noValidate>
            <div className="space-y-2">
              <Label htmlFor="email">{t('auth.email_label')}</Label>
              <Input
                id="email"
                type="text"
                autoComplete="username"
                autoFocus
                {...form.register('email')}
              />
              {form.formState.errors.email ? (
                <p className="text-sm text-destructive">{form.formState.errors.email.message}</p>
              ) : null}
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">{t('auth.password_label')}</Label>
              <SecretInput
                id="password"
                autoComplete="current-password"
                {...form.register('password')}
              />
              {form.formState.errors.password ? (
                <p className="text-sm text-destructive">{form.formState.errors.password.message}</p>
              ) : null}
            </div>
            {submit_error ? <p className="text-sm text-destructive">{submit_error}</p> : null}
            <Button type="submit" className="w-full" disabled={loading}>
              {loading ? t('common.loading') : t('auth.sign_in')}
            </Button>
          </form>

          {workstation_name ? (
            <p className="mt-6 text-center text-xs text-muted-foreground">{workstation_name}</p>
          ) : null}
        </div>
      </section>
    </main>
  );
}
